import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { authService } from "./services/authService";

// Auth Redirect Component
function AuthRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleAuth = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get("code");
        const error = params.get("error");
        const errorDescription = params.get("error_description");

        if (error) {
          throw new Error(errorDescription || error);
        }

        if (!code) {
          throw new Error("No authorization code received");
        }

        const tokenData = await authService.handleAuthCallback(code);
        sessionStorage.setItem("authData", JSON.stringify(tokenData));
        navigate("/");
      } catch (error) {
        console.error("Authentication error:", error);
        setError(error.message);
        navigate("/", { state: { error: "Authentication failed" } });
      }
    };

    handleAuth();
  }, [location, navigate]);

  if (error) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate("/")}>Return to Home</button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "center", marginTop: "50px" }}>
      <h2>Processing authentication...</h2>
      <p>Please wait while we complete your sign-in.</p>
    </div>
  );
}

// User Profile Component
function UserProfile({ userInfo, onLogout }) {
  if (!userInfo) {
    return (
      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}>
        <h2>Loading profile...</h2>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}>
      <h2>Welcome, {userInfo.name || userInfo.email}</h2>
      <div style={{ textAlign: "left", marginTop: "20px" }}>
        <p><strong>Email:</strong> {userInfo.email}</p>
        {userInfo.given_name && <p><strong>First Name:</strong> {userInfo.given_name}</p>}
        {userInfo.family_name && <p><strong>Last Name:</strong> {userInfo.family_name}</p>}
        {userInfo.phone_number && <p><strong>Phone:</strong> {userInfo.phone_number}</p>}
      </div>
      <button 
        onClick={onLogout}
        style={{
          marginTop: "20px",
          padding: "10px 20px",
          backgroundColor: "#ff4444",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer"
        }}
      >
        Logout
      </button>
    </div>
  );
}

// Main App Component
function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      try {
        const authData = authService.validateAuth();
        if (authData) {
          setUser(authData);
        }
      } catch (error) {
        console.error("Auth validation error:", error);
        setError("Failed to validate authentication");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location]);

  const handleLogin = () => {
    authService.login();
  };

  const handleLogout = () => {
    authService.logout();
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h2>Loading...</h2>
        <p>Please wait while we check your authentication status.</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div style={{ textAlign: "center", marginTop: "50px" }}>
            <h1>Welcome to the Burger Login App</h1>
            {error && (
              <div style={{ color: "red", marginBottom: "20px" }}>
                {error}
              </div>
            )}
            {user ? (
              <UserProfile userInfo={user.userInfo} onLogout={handleLogout} />
            ) : (
              <button 
                onClick={handleLogin}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Login with Azure B2C
              </button>
            )}
          </div>
        }
      />
      <Route path="/auth" element={<AuthRedirect />} />
    </Routes>
  );
}

export default App;